import React, { useEffect } from 'react';
import { Mail, Phone, Linkedin, Github, Award, Star, Briefcase, User, Code } from 'lucide-react';
import SEOHead from '../components/SEOHead';

const ResumePage = () => {
  useEffect(() => {
    document.title = 'Timothy Ljunggren - Resume';
  }, []);

  return (
    <div className="container mx-auto px-4 py-8 font-sans">
      <SEOHead 
        title="Timothy Ljunggren - Resume | Business Informatics Student"
        description="Download Timothy Ljunggren's resume. Business Informatics student with experience in React, Python, Machine Learning, leadership, and project management. Based in Berlin, Germany."
        keywords="Timothy Ljunggren, Resume, CV, Business Informatics, TU Berlin, React, Python, Machine Learning, Leadership, Berlin, Developer"
      />
      <div className="max-w-4xl mx-auto bg-gray-800 rounded-lg shadow-lg p-8">
        <header className="text-center mb-12">
          <h1 className="text-5xl font-bold text-white">Timothy Ljunggren</h1>
          <p className="text-xl text-gray-300 mt-2">Business Informatics student</p>
          <div className="flex justify-center flex-wrap gap-x-6 gap-y-2 mt-4 text-gray-400">
            <a href="mailto:timothy@antimala.de" className="hover:text-primary flex items-center"><Mail className="mr-2" /> timothy@antimala.de</a>
            <a href="tel:+4915164581384" className="hover:text-primary flex items-center"><Phone className="mr-2" /> +49 1516 4581384</a>
            <a href="https://www.linkedin.com/in/timothy-ljunggren/" target="_blank" rel="noopener noreferrer" className="hover:text-primary flex items-center"><Linkedin className="mr-2" /> LinkedIn</a>
            <a href="https://github.com/timothy-ljunggren" target="_blank" rel="noopener noreferrer" className="hover:text-primary flex items-center"><Github className="mr-2" /> GitHub</a>
          </div>
        </header>

        <section className="mb-10">
          <h2 className="text-3xl font-bold border-b-2 border-primary pb-2 mb-6 flex items-center"><User className="mr-3" /> About</h2>
          <div className="text-gray-300 space-y-4">
            <p className="text-lg leading-relaxed text-justify">
              Business Informatics student at TU Berlin with a strong foundation in computer science and mathematics. I bridge technology and business, combining technical expertise with strategic thinking to drive digital innovation. With leadership experience in retail and a growing portfolio of technical projects, I aim to create meaningful business solutions that support digital transformation. I thrive in collaborative environments where analytical skills and teamwork deliver real impact.
            </p>
          </div>
        </section>

        <section className="mb-10">
          <h2 className="text-3xl font-bold border-b-2 border-primary pb-2 mb-6 flex items-center"><Star className="mr-3" /> Education</h2>
          <div className="relative border-l-4 border-primary-dark pl-8 space-y-8 py-4">
            <div className="relative">
              <div className="absolute -left-[42px] top-1 w-4 h-4 bg-gray-800 border-4 border-primary rounded-full"></div>
              
              {/* Header: Role + Date */}
              <div className="flex items-start justify-between">
                <h4 className="text-xl font-bold text-white">B.Sc. in Business Informatics</h4>
                <p className="text-sm font-medium text-gray-400 ml-4 flex-shrink-0">2025 - Present</p>
              </div>
              
              {/* Institution Details */}
              <div className="mb-4">
                <h3 className="text-base font-medium text-gray-300">Technical University of Berlin, Berlin</h3>
              </div>
            </div>
            
            <div className="relative">
              <div className="absolute -left-[42px] top-1 w-4 h-4 bg-gray-800 border-4 border-primary rounded-full"></div>
              
              {/* Header: Role + Date */}
              <div className="flex items-start justify-between">
                <h4 className="text-xl font-bold text-white">B.Sc. in Computer Science, Mathematics, and Physics</h4>
                <p className="text-sm font-medium text-gray-400 ml-4 flex-shrink-0">2024 - 2025</p>
              </div>
              
              {/* Institution Details */}
              <div className="mb-4">
                <h3 className="text-base font-medium text-gray-300">Humboldt University of Berlin, Berlin</h3>
                <p className="text-gray-400 mb-2">Grade before transition: 1.5</p>
                <p className="text-gray-400">Gained foundational knowledge in computer science, mathematics, and physics.<br/>Transitioned to Business Informatics to better align with personal interests and career aspirations.</p>
              </div>
            </div>
            
            <div className="relative">
              <div className="absolute -left-[42px] top-1 w-4 h-4 bg-gray-800 border-4 border-primary rounded-full"></div>
              
              {/* Header: Role + Date */}
              <div className="flex items-start justify-between">
                <h4 className="text-xl font-bold text-white">Abitur (University Entrance Qualification)</h4>
                <p className="text-sm font-medium text-gray-400 ml-4 flex-shrink-0">2024</p>
              </div>
              
              {/* Institution Details */}
              <div className="mb-4">
                <h3 className="text-base font-medium text-gray-300">Kurt-Tucholsky-Oberschule, Berlin</h3>
                <p className="text-gray-400 mb-2">Grade: 1.0</p>
                <p className="text-gray-400">Advanced courses: Mathematics, English<br/>Exam subjects: Physics, History, Computer Science</p>
              </div>
            </div>
          </div>
        </section>

        <section className="mb-10">
          <h2 className="text-3xl font-bold border-b-2 border-primary pb-2 mb-6 flex items-center"><Briefcase className="mr-3" /> Professional Experience</h2>
           <div className="relative border-l-4 border-primary-dark pl-8 space-y-8 py-4">
             <div className="relative">
                <div className="absolute -left-[42px] top-1 w-4 h-4 bg-gray-800 border-4 border-primary rounded-full"></div>
                
                {/* Header: Role + Date */}
                <div className="flex items-start justify-between">
                  <h4 className="text-xl font-bold text-white">Sales Associate & Acting Deputy Manager</h4>
                  <p className="text-sm font-medium text-gray-400 ml-4 flex-shrink-0">Aug 2024 - Sep 2025</p>
                </div>
                
                {/* Company Details */}
                <div className="mb-4">
                  <h3 className="text-base font-medium text-gray-300 mb-2">Netto Marken-Discount, Berlin</h3>
                  <p className="text-gray-400 mb-3">I managed daily store operations and acted as substitute for store or deputy managers, ensuring smooth workflows, team coordination, and achievement of sales and operational goals.</p>
                  <ul className="list-disc list-inside text-gray-400 space-y-2 ml-6">
                    <li><strong className='text-gray-300'>Led a team of up to 5</strong>, delegating tasks and monitoring performance to ensure continuity during management absences; motivated staff and resolved issues, achieving daily sales and service targets.</li>
                    <li><strong className='text-gray-300'>Adapted staff assignments</strong> on short notice for absences or revenue shifts; reorganized shifts to maintain service quality and minimize disruption.</li>
                    <li><strong className='text-gray-300'>Monitored inventory</strong> through regular stock checks and reconciliations; applied systematic tracking to reduce shortages and write-offs.</li>
                    <li><strong className='text-gray-300'>Trained and onboarded new employees</strong> in processes, cash handling, and compliance; provided feedback that improved efficiency and reduced onboarding time.</li>
                    <li><strong className='text-gray-300'>Oversaw financial/operational processes</strong> including cash reconciliation, returns, and policy compliance; applied structured checks to safeguard accuracy and accountability.</li>
                  </ul>
                </div>
            </div>
            
             <div className="relative">
                <div className="absolute -left-[42px] top-1 w-4 h-4 bg-gray-800 border-4 border-primary rounded-full"></div>
                
                {/* Header: Role + Date */}
                <div className="flex items-start justify-between">
                  <h4 className="text-xl font-bold text-white">Sales Associate</h4>
                  <p className="text-sm font-medium text-gray-400 ml-4 flex-shrink-0">Jan 2024 - Jul 2024</p>
                </div>
                
                {/* Company Details */}
                <div className="mb-4">
                  <h3 className="text-base font-medium text-gray-300 mb-2">Netto Marken-Discount, Berlin</h3>
                  <p className="text-gray-400 mb-3">Supported daily store operations with a focus on customer service, sales transactions, and maintaining product availability.</p>
                  <ul className="list-disc list-inside text-gray-400 space-y-2 ml-6">
                    <li>Operated checkout systems and handled cash transactions accurately.</li>
                    <li>Restocked shelves and ensured product presentation met company standards.</li>
                    <li>Assisted customers with inquiries and maintained a clean, organized store environment.</li>
                  </ul>
                </div>
            </div>
          </div>
        </section>

        <section className="mb-10">
          <h2 className="text-3xl font-bold border-b-2 border-primary pb-2 mb-6 flex items-center"><Award className="mr-3" /> Certifications</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-gray-700/20 rounded-lg p-4 border-l-4 border-primary">
              <div className="flex items-center gap-3">
                <div>
                  <h3 className="font-semibold text-white">Abitur Prize in Mathematics</h3>
                  <p className="text-gray-400 text-sm">German Mathematical Society</p>
                </div>
              </div>
            </div>
            <div className="bg-gray-700/20 rounded-lg p-4 border-l-4 border-tertiary">
              <div className="flex items-center gap-3">
                <div>
                  <h3 className="font-semibold text-white">Google AI Essentials</h3>
                  <p className="text-gray-400 text-sm">Google</p>
                </div>
              </div>
            </div>
            <div className="bg-gray-700/20 rounded-lg p-4 border-l-4 border-primary">
              <div className="flex items-center gap-3">
                <div>
                  <h3 className="font-semibold text-white">Google Data Analytics (German)</h3>
                  <p className="text-gray-400 text-sm">Google</p>
                </div>
              </div>
            </div>
            <div className="bg-gray-700/20 rounded-lg p-4 border-l-4 border-accent">
              <div className="flex items-center gap-3">
                <div>
                  <h3 className="font-semibold text-white">Google Project Management</h3>
                  <p className="text-gray-400 text-sm">Google</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section>
          <h2 className="text-3xl font-bold border-b-2 border-primary pb-2 mb-6 flex items-center"><Code className="mr-3" /> Skills & Expertise</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div>
              <h3 className="text-xl font-bold mb-3">Frontend Development</h3>
              <div className="flex flex-wrap gap-2">
                <span className="bg-primary-dark text-white px-3 py-1 rounded-full text-sm font-medium">HTML5</span>
                <span className="bg-primary-dark text-white px-3 py-1 rounded-full text-sm font-medium">CSS3</span>
                <span className="bg-primary-dark text-white px-3 py-1 rounded-full text-sm font-medium">JavaScript</span>
                <span className="bg-primary-dark text-white px-3 py-1 rounded-full text-sm font-medium">React.js</span>
                <span className="bg-primary-dark text-white px-3 py-1 rounded-full text-sm font-medium">Tailwind CSS</span>
              </div>
            </div>
            <div>
              <h3 className="text-xl font-bold mb-3">App Development</h3>
              <div className="flex flex-wrap gap-2">
                <span className="bg-primary-dark text-white px-3 py-1 rounded-full text-sm font-medium">React Native</span>
                <span className="bg-primary-dark text-white px-3 py-1 rounded-full text-sm font-medium">TypeScript</span>
              </div>
            </div>
            <div>
              <h3 className="text-xl font-bold mb-3">Machine Learning</h3>
              <div className="flex flex-wrap gap-2">
                <span className="bg-primary-dark text-white px-3 py-1 rounded-full text-sm font-medium">Python</span>
                <span className="bg-primary-dark text-white px-3 py-1 rounded-full text-sm font-medium">NumPy</span>
                <span className="bg-primary-dark text-white px-3 py-1 rounded-full text-sm font-medium">PyTorch</span>
                <span className="bg-primary-dark text-white px-3 py-1 rounded-full text-sm font-medium">Jupyter Notebook</span>
                <span className="bg-primary-dark text-white px-3 py-1 rounded-full text-sm font-medium">Gradio</span>
              </div>
            </div>
            <div>
              <h3 className="text-xl font-bold mb-3">Backend Development</h3>
              <div className="flex flex-wrap gap-2">
                 <span className="bg-primary-dark text-white px-3 py-1 rounded-full text-sm font-medium">Node.js</span>
                 <span className="bg-primary-dark text-white px-3 py-1 rounded-full text-sm font-medium">MySQL</span>
                 <span className="bg-primary-dark text-white px-3 py-1 rounded-full text-sm font-medium">PHP</span>
                 <span className="bg-primary-dark text-white px-3 py-1 rounded-full text-sm font-medium">MongoDB</span>
              </div>
            </div>
            <div>
              <h3 className="text-xl font-bold mb-3">Tools & Platforms</h3>
              <div className="flex flex-wrap gap-2">
                <span className="bg-primary-dark text-white px-3 py-1 rounded-full text-sm font-medium">Azure DevOps</span>
                <span className="bg-primary-dark text-white px-3 py-1 rounded-full text-sm font-medium">Git</span>
                <span className="bg-primary-dark text-white px-3 py-1 rounded-full text-sm font-medium">GitHub</span>
                <span className="bg-primary-dark text-white px-3 py-1 rounded-full text-sm font-medium">VS Code</span>
              </div>
            </div>
            <div>
              <h3 className="text-xl font-bold mb-3">Leadership & Management</h3>
              <div className="flex flex-wrap gap-2">
                <span className="bg-accent text-white px-3 py-1 rounded-full text-sm font-medium">Leadership</span>
                <span className="bg-accent text-white px-3 py-1 rounded-full text-sm font-medium">Project Management</span>
                <span className="bg-accent text-white px-3 py-1 rounded-full text-sm font-medium">Agile Methodologies</span>
                <span className="bg-accent text-white px-3 py-1 rounded-full text-sm font-medium">Management</span>
              </div>
            </div>
            <div>
              <h3 className="text-xl font-bold mb-3">Collaboration & Communication</h3>
              <div className="flex flex-wrap gap-2">
                <span className="bg-accent text-white px-3 py-1 rounded-full text-sm font-medium">Teamwork</span>
                <span className="bg-accent text-white px-3 py-1 rounded-full text-sm font-medium">Problem Solving</span>
                <span className="bg-accent text-white px-3 py-1 rounded-full text-sm font-medium">Customer Service</span>
                <span className="bg-accent text-white px-3 py-1 rounded-full text-sm font-medium">Communication</span>
              </div>
            </div>
            <div>
              <h3 className="text-xl font-bold mb-3">Languages</h3>
              <div className="flex flex-wrap gap-2">
                <span className="bg-secondary-dark text-white px-3 py-1 rounded-full text-sm font-medium">German (Native)</span>
                <span className="bg-secondary-dark text-white px-3 py-1 rounded-full text-sm font-medium">English (C1)</span>
                <span className="bg-secondary-dark text-white px-3 py-1 rounded-full text-sm font-medium">Latin</span>
              </div>
            </div>
             <div>
              <h3 className="text-xl font-bold mb-3">Microsoft Office</h3>
              <div className="flex flex-wrap gap-2">
                <span className="bg-tertiary-dark text-white px-3 py-1 rounded-full text-sm font-medium">Word</span>
                <span className="bg-tertiary-dark text-white px-3 py-1 rounded-full text-sm font-medium">PowerPoint</span>
                <span className="bg-tertiary-dark text-white px-3 py-1 rounded-full text-sm font-medium">Excel</span>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default ResumePage;